import { BaseUser, BaseUserBuilder } from '@nab/nab-x-sdk-core';
import type {
    IUser,
    IDigitalDataEvent,
    IDataSecurityMgr,
    DataSecurityAlias,
    UserJson,
    ICoreFlagMgr,
    IUserRelevantAppDetails,
    IDataSecurityConsumables,
    IUserTailoring,
    IDigitalDataPageEvent,
    IUserBaseEvent,
    IInteractionEvent,
    IInteractionMgr,
    ITrackEvent
} from '@nab/x-types';
import { v4 as uuidv4 } from 'uuid';
import { AnalyticsMgr } from '../AnalyticsMgr';
import { TrackEvent } from '../AnalyticsMgr/TrackEvent';
import { IUserBuilder } from './types';
import { UserTailoring } from './UserTailoring';
import { Events } from '../common/events';

/**
 * [[include:packages/consumer/sdk-browser/src/User/README.md]]
 *
 * @todo Make constructor params an object - this will ensure no breaking change is required to add an additional param
 *
 * interface IUserParams {
 *     user: UserJson;
 *     app: IUserRelevantAppDetails;
 *     services: {
 *         analyticsMgr: AnalyticsMgr;
 *         dataSecurity: IDataSecurityMgr;
 *         flagMgr: ICoreFlagMgr;
 *         // ...future services
 *     }
 *     // ...future params
 * }
 *
 */
export class User extends BaseUser implements IUser {
    public readonly tailoring: IUserTailoring = new UserTailoring(this.app?.appMetaData.id || 'SHELL', this, this.baseFlagMgr);

    public readonly trackEvent: ITrackEvent = new TrackEvent(this.emitEvent.bind(this));

    private constructor(
        private user: UserJson,
        private app: IUserRelevantAppDetails,
        private readonly analyticsMgr: AnalyticsMgr,
        private readonly dataSecurityInstance: IDataSecurityMgr,
        private readonly baseFlagMgr: ICoreFlagMgr,
        private readonly interactionMgr: IInteractionMgr
    ) {
        super(user);
    }

    /**
     * The User emitEvent is build to support passing data to any service that can make use of it.
     * Currently it supports publishing events to the Digital Data layer exposed via `window.digitalData` object to capture analytics and marketing data.
     * @param payload Event payload can be managed via generics but the default types supported is defined by @interface IDigitalDataEvent or @interface IDigitalDataPageEvent format to be published to the services
     * @returns return a boolean value of success or failure
     */
    public emitEvent<T extends IUserBaseEvent = IDigitalDataEvent>(payload: T): Promise<boolean> {
        switch ((payload as unknown as IUserBaseEvent).eventType) {
            case Events.USER_INTERACTION:
                return this.handleInteractionEvent<T>(payload);
            default:
                return this.handleAnalyticsEvent<T>(payload);
        }
    }

    private handleInteractionEvent<T = IInteractionEvent>(payload: T) {
        return this.interactionMgr.handleInteraction((payload as unknown as IInteractionEvent).data);
    }

    private handleAnalyticsEvent<T = IDigitalDataEvent>(payload: T) {
        /**
         * This allows the miniapp to update the digital data with page object, in a special condition.
         * It does not require us to add contextual data.
         */
        if ((payload as unknown as IDigitalDataPageEvent).eventType === 'update-page') {
            return this.analyticsMgr.updatePage((payload as unknown as IDigitalDataPageEvent).data);
        }

        /**
         * Based on the usage of the context data especially in digitalData where the appMetaData is not yet put in use,
         * but the parent context is used to override the details of events passed from inner/embedded miniapps.
         * Refer this confluence link below, where colour coded section for "DigitalData Object - Example"
         * https://confluence.dss.ext.national.com.au/display/NABX/NAB-X+-+contextual+events
         *
         * Hence we are extracting the appMetaData details be part of NAB-X introduced appMetaData key,
         * but the rest of the data is added directly to the parent data object.
         * */
        const { appMetaData: parentAppMetadata, ...parentContextData } = this.app?.appMetaData?.parentContext || {};

        const payloadWithContextAndAppMetaData = {
            ...(payload as unknown as IDigitalDataEvent),
            data: {
                ...(payload as unknown as IDigitalDataEvent).data,
                /** extracting all context data provided at any level to be added at root data */
                ...parentContextData,
                ...this.context,

                /** This app metadata added for the reference to know which app has been loaded in what sequence */
                ...(this.app && {
                    appMetaData: {
                        id: this.app.appMetaData.id,
                        name: this.app.appMetaData.name,
                        route: this.app.appMetaData.route,
                        description: this.app.appMetaData.description,
                        ...(parentAppMetadata && { parentContext: { appMetaData: parentAppMetadata } })
                    }
                })
            }
        };
        return this.analyticsMgr.logEvent<IDigitalDataEvent>(payloadWithContextAndAppMetaData);
    }

    /**
     * @deprecated This function will be deprecated in favour of the new function `user.dataSecurity` that will pass the object instance of shareable functions.
     */
    public dataSecurityInfo(securityAlias: DataSecurityAlias | string): Promise<string> {
        return this.dataSecurityInstance[securityAlias].getData();
    }

    /**
     * Use this function to get the instance of the data security library with functions that can be used.
     * @param securityAlias The fraud library alias to retrieve the appropriate fraud library instance.
     * @returns Object of usable functions and values to be consumed from the library.
     * @throws library not initialised error if the security alias is incorrect or the shell has not initialised the library
     */
    public dataSecurity(securityAlias: DataSecurityAlias | string): IDataSecurityConsumables {
        const dataSecurityLib = this.dataSecurityInstance[securityAlias];
        if (dataSecurityLib) {
            // It was needed to bind the context of this to the functions extracted as it looses the other context in the new object
            const { getData, setContext } = dataSecurityLib;
            return {
                getData: getData.bind(dataSecurityLib),
                setContext: setContext.bind(dataSecurityLib)
            };
        }
        throw new Error(`Data security library ${securityAlias}, not initialised`);
    }

    public static fromJSON(
        json: UserJson,
        app: IUserRelevantAppDetails,
        analyticsMgr?: AnalyticsMgr,
        dataSecurity?: IDataSecurityMgr,
        baseFlagMgr?: ICoreFlagMgr,
        interactionMgr?: IInteractionMgr
    ): IUser {
        return new User(json, app, analyticsMgr, dataSecurity, baseFlagMgr, interactionMgr);
    }

    public forkForApp(app: IUserRelevantAppDetails): IUser {
        return new User(this.user, app, this.analyticsMgr, this.dataSecurityInstance, this.baseFlagMgr, this.interactionMgr);
    }

    public generateTraceId(): string {
        return uuidv4();
    }
}

/**
 * [[include:packages/consumer/sdk-browser/src/User/UserBuilder.md]]
 */
export class UserBuilder extends BaseUserBuilder<User> implements IUserBuilder<User> {
    protected json: UserJson;

    constructor(
        user?: User | UserJson,
        private appObj?: IUserRelevantAppDetails,
        private analyticsMgrObj?: AnalyticsMgr,
        private dataSecurityObj?: IDataSecurityMgr,
        private flagMgrObj?: ICoreFlagMgr,
        private interactionMgrObj?: IInteractionMgr
    ) {
        super(user);
    }

    public app(value: IUserRelevantAppDetails): UserBuilder {
        this.appObj = value;
        return this;
    }

    public analyticsManager(value: AnalyticsMgr): UserBuilder {
        this.analyticsMgrObj = value;
        return this;
    }

    public dataSecurityManager(value: IDataSecurityMgr): UserBuilder {
        this.dataSecurityObj = value;
        return this;
    }

    public flagMgr(value: ICoreFlagMgr): UserBuilder {
        this.flagMgrObj = value;
        return this;
    }

    public interactionManager(value: IInteractionMgr): UserBuilder {
        this.interactionMgrObj = value;
        return this;
    }

    build(): User {
        const { json, appObj, analyticsMgrObj, dataSecurityObj, flagMgrObj, interactionMgrObj } = this;
        return Object.freeze<any>(User.fromJSON(json, appObj, analyticsMgrObj, dataSecurityObj, flagMgrObj, interactionMgrObj));
    }
}
